<div class="time-off-policy-edit-dialog">
  <div class="dialog-header">
    <h2 class="dialog-title">{{ dialogTitle }}</h2>
    <button
      mat-icon-button
      disableRipple
      class="close-button"
      tabindex="-1"
      (click)="close()"
      aria-label="Close"
    >
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <mat-dialog-content class="dialog-content">
    @if (isLoading) {
      <div class="loading-state">
        <mat-spinner diameter="32"></mat-spinner>
      </div>
    } @else {
      <div
        class="mode-selector"
        [class.mode-selector--end]="mode === 'policy'"
      >
        <span class="mode-selector__thumb" aria-hidden="true"></span>
        <button
          type="button"
          class="mode-card"
          [class.active]="mode === 'currentYear'"
          [attr.aria-pressed]="mode === 'currentYear'"
          [disabled]="!isAdjustmentSupported"
          (click)="requestMode('currentYear')"
        >
          <mat-icon class="mode-card__icon">edit_calendar</mat-icon>
          <span class="mode-card__text">
            <span class="mode-card__title">{{ setDaysTitle }}</span>
            <span class="mode-card__sub">
              @if (isAdjustmentSupported) {
                Set the total available for the current year (<strong>{{
                  currentYear
                }}</strong
                >)
              } @else {
                Only vacation adjustments are supported today
              }
            </span>
          </span>
        </button>
        <button
          type="button"
          class="mode-card"
          [class.active]="mode === 'policy'"
          [attr.aria-pressed]="mode === 'policy'"
          (click)="requestMode('policy')"
        >
          <mat-icon class="mode-card__icon">event_upcoming</mat-icon>
          <span class="mode-card__text">
            <span class="mode-card__title">Change This User's Policy</span>
            <span class="mode-card__sub"
              >Update the user's policy for entitled days</span
            >
          </span>
        </button>
      </div>

      <div class="mode-divider" aria-hidden="true"></div>

      <!-- Both sections stay rendered, stacked in the same grid cell; the
           inactive one is visibility-hidden so the container always sizes to
           the taller section and the modal height never changes on switch. -->
      <div class="policy-sections">
        <section
          class="policy-section"
          [class.policy-section--hidden]="mode !== 'currentYear'"
        >
          <h3 class="section-heading">
            Current Year Total for
            <span class="section-heading__year">{{ currentYear }}</span>
          </h3>

          <div class="policy-fields">
            <div class="policy-field">
              <label class="policy-field-label" for="current-year-input">
                Total days available
                <span class="policy-field-label__detail">
                  (currently: <strong>{{ currentYearBaseline }}</strong>)
                </span>
              </label>
              <mat-form-field appearance="outline" subscriptSizing="dynamic">
                <input
                  id="current-year-input"
                  matInput
                  type="number"
                  step="1"
                  min="0"
                  [max]="maxDays"
                  [(ngModel)]="currentYearTotal"
                  #adjustmentModel="ngModel"
                  [errorStateMatcher]="adjustmentErrorMatcher"
                />
                @if (adjustmentModel.touched || adjustmentModel.dirty) {
                  @if (daysError(currentYearTotal); as err) {
                    <mat-error>{{ err }}</mat-error>
                  }
                }
              </mat-form-field>
            </div>
          </div>

          <div class="policy-intro">
            <mat-icon class="policy-intro__icon">info</mat-icon>
            <p class="policy-intro__text">
              This sets the employee's total available balance for the current
              year — it replaces the current amount. Carried-over days expire
              March 31, so a total set before then will drop by any that go
              unused.
            </p>
          </div>
        </section>
        <section
          class="policy-section"
          [class.policy-section--hidden]="mode === 'currentYear'"
        >
          <h3 class="section-heading">
            Edit Policy for
            <span class="section-heading__year"
              >{{ nextYear }} and future years</span
            >
          </h3>

          <div class="policy-fields">
            <div class="policy-field">
              <label class="policy-field-label" for="future-input">
                Policy days per year
                <span class="policy-field-label__detail">
                  (currently: <strong>{{ futureBaseline }}</strong>)
                </span>
              </label>
              <mat-form-field appearance="outline" subscriptSizing="dynamic">
                <input
                  id="future-input"
                  matInput
                  type="number"
                  step="1"
                  min="0"
                  [max]="maxDays"
                  [(ngModel)]="futureTotal"
                  #futureModel="ngModel"
                  [errorStateMatcher]="futureErrorMatcher"
                />
                @if (futureModel.touched || futureModel.dirty) {
                  @if (daysError(futureTotal); as err) {
                    <mat-error>{{ err }}</mat-error>
                  }
                }
              </mat-form-field>
            </div>
          </div>

          @if (!isBereavement && data.officeDefaultEntitledDays != null) {
            <div class="policy-intro">
              <mat-icon class="policy-intro__icon">info</mat-icon>
              <p class="policy-intro__text">
                @if (inheritedFromPracticeArea) {
                  <!-- The number is the practice area's, not the office's, so name the
                       right source — and point the admin at the overrides, not the
                       office default, when they go to change it. -->
                  This resource's
                  <strong>{{ practiceAreaName }}</strong> practice area overrides
                  the office default with
                  <strong>{{ data.officeDefaultEntitledDays }}</strong>
                  {{ availableDaysLabel | lowercase }}.
                  @if (data.officeCode) {
                    To change it, edit the practice area overrides on the office
                    details page
                    <a
                      class="policy-intro__link"
                      [href]="practiceAreaOverridesUrl"
                      target="_blank"
                      rel="noopener"
                      >here</a
                    >.
                  }
                } @else {
                  This resource's office has a default of
                  <strong>{{ data.officeDefaultEntitledDays }}</strong>
                  {{ availableDaysLabel | lowercase }}.
                  @if (data.officeCode) {
                    To change the office default, visit the office details page
                    <a
                      class="policy-intro__link"
                      [href]="officeDetailsUrl"
                      target="_blank"
                      rel="noopener"
                      >here</a
                    >.
                  }
                }
              </p>
            </div>
          }
        </section>
      </div>

      <div class="content-divider" aria-hidden="true"></div>

      <section class="note-section manager-note-input-section">
        <h3 class="note-section-title">
          Notes
          <span class="note-label-hint"> (optional) </span>
        </h3>
        <textarea
          class="manager-note-textarea"
          [(ngModel)]="notesInput"
          rows="5"
          placeholder="Add notes about this current year adjustment and/or future policy change..."
        ></textarea>
      </section>
    }
  </mat-dialog-content>

  <div class="dialog-actions">
    <button
      type="button"
      mat-flat-button
      class="save-button"
      [disabled]="!canSave"
      (click)="save()"
    >
      @if (isSaving) {
        <mat-spinner diameter="18"></mat-spinner>
      } @else {
        Save
      }
    </button>
  </div>
</div>
